import { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaMagnifyingGlass, FaTags } from "react-icons/fa6";
import { LogisticCategoryService } from "../../../services/LogisticCategoryService";

export const SelectLogisticCategory = ({
    label,
    value,
    setValue,
    placeholder = "Pilih Kategori",
    searchable = true,
    disabled = false,
}) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wrapperRef = useRef(null);

    const safeData = Array.isArray(data) ? data : [];

    // Fetch data kategori saat komponen di-mount
    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const res = await LogisticCategoryService.get({ params: {} });
                const result = res?.data?.data ?? res?.data ?? [];
                setData(Array.isArray(result) ? result : []);
            } catch (err) {
                console.error("Gagal memuat kategori:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Menutup dropdown jika klik di luar komponen
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selected = safeData.find((o) => o.id === value);

    const filteredOptions = safeData.filter((o) =>
        o?.name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className={`w-full relative ${disabled ? "opacity-50 pointer-events-none" : ""}`} ref={wrapperRef}>
            {label && (
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                    {label}
                </label>
            )}

            {/* Select Box */}
            <div
                onClick={() => !disabled && setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 cursor-pointer transition-all duration-200"
                style={{
                    borderColor: open ? "rgba(0, 180, 216, 0.5)" : "rgba(71, 85, 105, 0.4)",
                    boxShadow: open ? "0 0 0 1px rgba(0, 180, 216, 0.2)" : "none",
                }}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {!selected && (
                        <FaTags className="text-slate-500 text-xs flex-shrink-0" />
                    )}
                    {loading ? (
                        <i className="fa-solid fa-spinner fa-spin text-slate-500 text-xs" />
                    ) : (
                        <span
                            className={`text-ellipsis truncate capitalize ${
                                !selected ? "text-slate-500" : "text-gray-100"
                            }`}
                        >
                            {selected ? selected.name : placeholder}
                        </span>
                    )}
                </div>
                <FaChevronDown
                    className={`text-xs text-slate-500 flex-shrink-0 ml-2 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </div>

            {/* Dropdown Menu */}
            {open && (
                <div
                    className="absolute z-[1000] mt-2 w-full rounded-xl overflow-hidden shadow-2xl shadow-black/40"
                    style={{
                        background: "rgba(15, 23, 42, 0.95)",
                        border: "1px solid rgba(51, 65, 85, 0.5)",
                        backdropFilter: "blur(16px)",
                    }}
                >
                    {/* Input Pencarian */}
                    {searchable && (
                        <div
                            className="p-2.5"
                            style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.3)" }}
                        >
                            <div className="relative">
                                <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
                                <input
                                    type="text"
                                    placeholder="Cari kategori..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-800/80 border border-slate-600/30 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}

                    {/* List Options */}
                    <div className="max-h-56 overflow-y-auto py-1">
                        {/* Opsi Reset / Kosongkan */}
                        <div
                            onClick={() => {
                                setValue("");
                                setOpen(false);
                                setSearch("");
                            }}
                            className={`px-3 py-2.5 mx-1 rounded-lg cursor-pointer text-sm transition-all duration-150 flex items-center gap-2 ${
                                value === "" || !value
                                    ? "bg-cyan-600/15 text-cyan-400 font-semibold"
                                    : "text-slate-400 hover:bg-slate-700/40 hover:text-gray-100"
                            }`}
                        >
                            <FaTags className="text-xs flex-shrink-0" />
                            <span>Semua Kategori</span>
                        </div>

                        {/* Opsi Data */}
                        {loading ? (
                            <div className="px-3 py-6 text-center">
                                <i className="fa-solid fa-spinner fa-spin text-slate-500 text-sm" />
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="px-3 py-6 text-center">
                                <p className="text-slate-500 text-sm italic">
                                    Kategori tidak ditemukan
                                </p>
                            </div>
                        ) : (
                            filteredOptions.map((opt) => (
                                <div
                                    key={opt.id}
                                    onClick={() => {
                                        setValue(opt.id);
                                        setOpen(false);
                                        setSearch("");
                                    }}
                                    className={`px-3 py-2.5 mx-1 rounded-lg cursor-pointer text-sm transition-all duration-150 flex items-center gap-2 ${
                                        value === opt.id
                                            ? "bg-cyan-600/15 text-cyan-400 font-semibold"
                                            : "text-slate-300 hover:bg-slate-700/40 hover:text-gray-100"
                                    }`}
                                >
                                    <FaTags className="text-xs flex-shrink-0 opacity-50" />
                                    <span className="truncate capitalize">{opt.name}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};